import { motion } from 'framer-motion';
import { PiAirplaneInFlightBold } from 'react-icons/pi';

const flyAnimation1 = {
  initial: { x: '-100vw', y: 0 },
  animate: {
    x: '200vw',
    y: [-30, 20, -10],
    transition: {
      x: {
        duration: 35,
        repeat: Infinity,
        ease: "linear",
        repeatDelay: 15
      },
      y: {
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }
};

const flyAnimation2 = {
  initial: { x: '-100vw', y: 0 },
  animate: {
    x: '200vw',
    y: [10, -20, 30],
    transition: {
      x: {
        duration: 45,
        repeat: Infinity,
        ease: "linear",
        repeatDelay: 20
      },
      y: {
        duration: 12,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }
};

const cloudBobAnimation = {
  initial: { y: 0 },
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut",
      repeatType: "mirror" as const
    }
  }
};

interface CloudProps {
  top: string;
  scale?: number;
  delay?: number;
  duration?: number;
  initialX?: string;
  right?: string;
}

const Cloud: React.FC<CloudProps> = ({ top, scale = 1, delay = 0, duration = 60, initialX = '-100%', right }) => (
  <motion.div
    style={{
      position: 'fixed',
      top,
      left: right ? undefined : 0,
      right: right ? right : undefined,
      width: '300px',
      height: '100px',
      zIndex: -1,
      transform: `scale(${scale})`,
      opacity: 1,
      willChange: 'transform',
    }}
    initial={{ 
      x: initialX, 
      opacity: 1 
    }}
    animate={{ 
      x: '200%',
      opacity: [1, 1, 1, 0],
    }}
    transition={{
      x: {
        duration,
        delay,
        repeat: Infinity,
        ease: "linear",
      },
      opacity: {
        duration,
        delay,
        repeat: Infinity,
        times: [0, 0.1, 0.9, 1],
        ease: "linear",
      }
    }}
  >
    {/* Base cloud shape */}
    <motion.div
      style={{
        position: 'absolute',
        bottom: '0',
        left: '10%',
        width: '80%',
        height: '40%',
        background: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '100px',
        filter: 'blur(8px)',
        willChange: 'transform',
      }}
      variants={cloudBobAnimation}
      initial="initial"
      animate="animate"
    />
    {/* Cloud puffs */}
    <motion.div
      style={{
        position: 'absolute',
        bottom: '20%',
        left: '15%',
        width: '45%',
        height: '60%',
        background: 'rgba(255, 255, 255, 0.18)',
        borderRadius: '100%',
        filter: 'blur(8px)',
        willChange: 'transform',
      }}
      variants={cloudBobAnimation}
      initial="initial"
      animate="animate"
    />
    <motion.div
      style={{
        position: 'absolute',
        bottom: '20%',
        right: '15%',
        width: '45%',
        height: '60%',
        background: 'rgba(255, 255, 255, 0.18)',
        borderRadius: '100%',
        filter: 'blur(8px)',
        willChange: 'transform',
      }}
      variants={cloudBobAnimation}
      initial="initial"
      animate="animate"
    />
    <motion.div
      style={{
        position: 'absolute',
        bottom: '40%',
        left: '30%',
        width: '40%',
        height: '60%',
        background: 'rgba(255, 255, 255, 0.18)',
        borderRadius: '100%',
        filter: 'blur(8px)',
        willChange: 'transform',
      }}
      variants={cloudBobAnimation}
      initial="initial"
      animate="animate"
    />
  </motion.div>
);

export function SkyElements() {
  return (
    <>
      {/* Animated Airplanes */}
      <motion.div
        style={{
          position: 'fixed',
          top: '15%',
          left: 0,
          zIndex: -1,
          color: 'rgba(255, 255, 255, 0.08)',
          fontSize: '64px',
          transform: 'rotate(-5deg)',
          willChange: 'transform',
          opacity: 1,
        }}
        variants={flyAnimation1}
        initial="initial"
        animate="animate"
      >
        <PiAirplaneInFlightBold />
      </motion.div>

      <motion.div
        style={{
          position: 'fixed',
          top: '45%',
          left: 0,
          zIndex: -1,
          color: 'rgba(255, 255, 255, 0.06)',
          fontSize: '48px',
          transform: 'rotate(-10deg)',
          willChange: 'transform',
          opacity: 1,
        }}
        variants={flyAnimation2}
        initial="initial"
        animate="animate"
      >
        <PiAirplaneInFlightBold />
      </motion.div>

      {/* Clouds with adjusted initial states */}
      <Cloud top="30%" scale={0.9} initialX="20%" duration={120} right="20%" />
      <Cloud top="65%" scale={0.7} initialX="-20%" duration={100} right="60%" />
      <Cloud top="10%" scale={0.8} initialX="50%" duration={140} right="40%" />
    </>
  );
} 